import { Link } from "@inertiajs/react"

import AdminLayout from "../../../shared/AdminLayout"

interface AdminContactRow {
  id: number
  name: string
  channelsText: string | null
  editHref: string
}

interface AdminContactSection {
  heading: string
  contacts: AdminContactRow[]
}

interface AdminContactsProps {
  sections: AdminContactSection[]
  emptyText: string
  newHref: string
  addLabel: string
}

const ROW_CLASSES = "flex items-center gap-3 border-b border-gray-100 py-3"
const LAST_ROW_CLASSES = "flex items-center gap-3 py-3"

// The admin contacts list (`Contacts::AdminContactsController#index`,
// `/admin/contacts`). Implements docs/design/contacts.html's Block 3
// verbatim: the same category sections as the public page (FR-4.3), rows
// tap-to-edit in the landed admin idiom (one interactive zone, a chevron,
// no other control — AdminDays.tsx's own last-row-drops-its-border
// treatment reused here, computed per section since each section is its
// own bordered group), and a dashed «+ Добавить контакт» at the bottom —
// present even with zero contacts (FR-4.3), when the shared empty-state
// text renders in its place above it rather than any section chrome.
export default function AdminContacts({ sections, emptyText, newHref, addLabel }: AdminContactsProps) {
  return (
    <AdminLayout mainClassName="flex flex-col gap-6 p-4">
      {sections.length > 0 ? (
        sections.map((section) => (
          <section key={section.heading} className="flex flex-col gap-1">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {section.heading}
            </h2>
            <ol className="flex flex-col">
              {section.contacts.map((contact, index) => (
                <li key={contact.id}>
                  <Link
                    href={contact.editHref}
                    className={
                      index === section.contacts.length - 1 ? LAST_ROW_CLASSES : ROW_CLASSES
                    }
                  >
                    <div className="flex grow flex-col gap-px">
                      <div className="text-[15px] font-semibold leading-[22px] text-gray-900">
                        {contact.name}
                      </div>
                      {/* FR-4.4: channel names only, never a value — and
                          no second line at all for a channel-less
                          contact. */}
                      {contact.channelsText !== null && (
                        <div className="text-[13px] leading-[18px] text-gray-500">
                          {contact.channelsText}
                        </div>
                      )}
                    </div>
                    <svg
                      aria-hidden="true"
                      className="text-gray-400"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M6 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </li>
              ))}
            </ol>
          </section>
        ))
      ) : (
        <p className="px-6 py-16 text-center text-[15px] text-gray-500">{emptyText}</p>
      )}
      <Link
        href={newHref}
        className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-gray-300 text-sm font-medium text-accent"
      >
        {addLabel}
      </Link>
    </AdminLayout>
  )
}
